import {ReactElement} from 'react';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {SimpleTreeView} from '@mui/x-tree-view/SimpleTreeView';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import {resourceIcon} from '../../../utils/resource-icons';
import {ResourceType} from '../../../@types/resource-type';
import GroupActionsMenu from './../menus/Group.actions.menu';
import {TreeViewItem, TreeViewProps} from '../../../@types/tree-view-item';
import UserActionsMenu from '../menus/User.actions.menu';
import SafetyModelActionsMenu from '../menus/SafetyModel.actions.menu';
import EventSequenceActionsMenu from '../menus/EventSequence.actions.menu';
import {useWorkspaceContext} from '../../../context/workspaceContext';
import OccurrenceActionsMenu from '../menus/Occurrence.actions.menu';
import OccurrenceReportDescriptorActionsMenu from '../menus/OccurrenceReportDescriptor.actions.menu';
import PsfTemplateActionsMenu from '../menus/PsfTemplate.actions.menu';
import ServiceUserAccountActionsMenu from '../menus/ServiceUserAccount.actions.menu';
import {StyledTreeItem} from '../../form-components/StyledTreeItem.component';


export function ExplorerTreeView(props: TreeViewProps): ReactElement {
    const { title, nodes, onClickItem, exploreMode, ...other } = props;
    const { workspaceObjects } = useWorkspaceContext();

    const handleSingleClick = (event: any): void => {
        if (exploreMode)
            onClickItem(event);
    }

    const handleDoubleClick = (event: any): void => {
        if (!exploreMode)
            onClickItem(event);
    }

    const renderTreeItem = (node: TreeViewItem): ReactElement => {
        return (
            <StyledTreeItem tabIndex={0}  key={node.type + '-' + node.id} itemId={ node.type + '-' + node.id} label={

                <Box tabIndex={0} sx={{ display: 'flex', justifyContent: 'space-between', p: 0.5, pr: 0 }}
                    onClick={handleSingleClick} onDoubleClick={handleDoubleClick}>

                    <Typography sx={{ mr: 1 }} component="div" >
                        {resourceIcon(node.type)}
                    </Typography>

                    <Typography id={'box-' + node.type + '-' + node.id} variant="body2" sx={{ flexGrow: 1 }}>
                        {node.name}
                    </Typography>

                    {node.type === ResourceType.OCCURRENCE_REPORTS  &&
                        <Typography variant="caption" sx={{ mr: 1, mt: 0.5, color: '#606060', flexGrow: 1  }} >
                            {String.fromCodePoint(0x1F4C5) + ' ' + new Date(node.dateTime!).toLocaleDateString()}
                        </Typography>
                    }   

                    {node.type === ResourceType.OCCURRENCE_REPORTS &&
                        <Typography variant="caption" sx={{ mr: 1, mt: 0.5, color: '#606060', flexGrow: 1  }} >
                            {String.fromCodePoint(0x1F4CC) + ' ' + node.location}
                        </Typography>
                    }

                    <Typography variant="caption" sx={{ mr: 1, mt: 0.5, color: '#606060' }}>
                        {node.groupPath ? node.groupPath : null}
                    </Typography>

                    <Typography variant="caption" sx={{ mr: 1 }}>
                        {node.groupRole ? <Chip label={node.groupRole.toUpperCase()} variant="outlined" size="small" sx={{ color: '#606060', fontSize: '8px' }}/> : null}
                    </Typography>

                    {exploreMode && node.type === ResourceType.GROUPS  && workspaceObjects?.objectType === ResourceType.GROUPS &&
                        <GroupActionsMenu group={node.object} />
                    }

                    {exploreMode && node.type === ResourceType.USERS &&
                        <UserActionsMenu user={node.object} />
                    }

                    {exploreMode && node.type === ResourceType.SERVICE_USER_ACCOUNTS &&
                        <ServiceUserAccountActionsMenu user={node.object} />
                    }

                    {exploreMode && node.type === ResourceType.SAFETY_MODELS &&
                        <SafetyModelActionsMenu safetyModel={node.object} />
                    }

                    {exploreMode && node.type === ResourceType.EVENT_SEQUENCES &&
                        <EventSequenceActionsMenu eventSequenceSummary={node.object} />
                    }

                    {exploreMode && node.type === ResourceType.OCCURRENCE_REPORTS &&
                        <OccurrenceActionsMenu occurrence={node.object} />
                    }

                    {exploreMode && node.type === ResourceType.OCCURRENCE_REPORT_DESCRIPTORS &&
                        <OccurrenceReportDescriptorActionsMenu occurrenceReportDescriptor={node.object} />
                    }

                    {exploreMode && node.type === ResourceType.PERFORMANCE_SHAPING_FACTORS &&
                        <PsfTemplateActionsMenu psfTemplate={node.object} />
                    }

                </Box>
            }
            slots={{icon: !node.children && node.hasChildren ? ChevronRightIcon : undefined}}
            >
                {Array.isArray(node.children) ? node.children?.map((child) => renderTreeItem(child)) : null}
            </StyledTreeItem>
        );
    }

    return (
        <>
            {title? <Typography sx={{ mr: 1}} variant='h6' color='primary'>{title}</Typography> : null}
            <SimpleTreeView
                aria-label="tree-view"
                slots={{ expandIcon: ChevronRightIcon, collapseIcon: ExpandMoreIcon }}
                sx={{flexGrow: 1, overflowY: 'auto', width: '100%', overflowX: 'hidden' }}
                {...other}
            >
                {nodes.map((node) => renderTreeItem(node))}
            </SimpleTreeView>
        </>
    );
}